import { useSearchParams } from 'react-router-dom';
import { PageBand } from '../v2/shared';
import AdminRentalPayments from '../components/AdminRentalPayments';
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { Professional, Appointment, User, Service, ActivityLog, Room, SystemConfig, Specialty } from '../types';
import { 
  Users, 
  Calendar, 
  Clock, 
  Settings,
  UserPlus,
  Clock as ClockIcon,
  User as UserIcon,
  Edit,
  Trash2,
  Building
} from 'lucide-react';
import ImprovedAppointmentCalendar from '../components/ImprovedAppointmentCalendar';
import ProfessionalAgenda from '../components/ProfessionalAgenda';

import MonthlyRoomAvailabilityManager from '../components/MonthlyRoomAvailabilityManager';
import RoomOccupancy from '../components/RoomOccupancy';
import SpecialtiesManager from '../components/SpecialtiesManager';
import PreAppointmentsManager from '../components/PreAppointmentsManager';
import toast from 'react-hot-toast';
import { formatCurrency, convertISOToArgentine, convertArgentineToISO } from '../utils/formatters';
import ArgentineDatePicker from '../components/ArgentineDatePicker';
import { motion } from 'framer-motion';
import { RoomTemplate, generateDefaultTemplates } from '../utils/roomTemplateUtils';

interface AdminDashboardProps {}

const AdminDashboard: React.FC<AdminDashboardProps> = () => {
  useAuth();
  const [activeTab, setActiveTab] = useState<'professionals' | 'appointments' | 'users' | 'system' | 'reports'>('professionals');
  const [activeSubTab, setActiveSubTab] = useState<string>('');
  const [agendaProfessionalId, setAgendaProfessionalId] = useState<string>('');
  // links in emails open the right place: /admin?ver=turnos
  const [searchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('ver') === 'turnos') { setActiveTab('appointments'); setActiveSubTab('calendar'); }
  }, [searchParams]);
  const [attention, setAttention] = useState<{ roomGaps: Array<{ roomId: number; roomName: string; year: number; month: number }>; pendingRequests: number }>({ roomGaps: [], pendingRequests: 0 });
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState<Service[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [systemConfig, setSystemConfig] = useState<SystemConfig | null>(null);
  const [rateSteps, setRateSteps] = useState<Array<{ from: string; hourly: number }>>([]);
  const [newStep, setNewStep] = useState<{ from: string; hourly: string }>({ from: '', hourly: '' });
  const [timezoneConfig, setTimezoneConfig] = useState({
    timezone: 'America/Argentina/Buenos_Aires',
    locale: 'es-AR',
    currency: 'ARS',
    country_code: '+54',
    default_start_time: '09:00',
    default_end_time: '17:00',
    lunch_start: '12:00',
    lunch_end: '13:00',
    week_starts_on: 'monday' as 'monday' | 'sunday'
  });
  const [showCreateDeductionRule, setShowCreateDeductionRule] = useState(false);
  const [showEditDeductionRule, setShowEditDeductionRule] = useState(false);
  const [editingDeductionRuleId, setEditingDeductionRuleId] = useState<string | null>(null);
  const [newDeductionRule, setNewDeductionRule] = useState({
    minHoursPerDay: 0,
    discountPercentage: 0,
    description: ''
  });
  const [showCreateProfessional, setShowCreateProfessional] = useState(false);
  const [newProfessional, setNewProfessional] = useState({
    email: '',
    password: '',
    name: '',
    specialty_id: 0, // main specialty
    specialty_ids: [] as number[], // every specialty the professional attends
    phone: '',
    bio: '',
    sortOrder: 0
  });
  const [showEditProfessional, setShowEditProfessional] = useState(false);
  const [editingProfessional, setEditingProfessional] = useState<Professional | null>(null);
  const [editProfessional, setEditProfessional] = useState({
    name: '',
    email: '',
    specialty_id: 0, // main specialty
    specialty_ids: [] as number[],
    bio: '',
    phone: '',
    sortOrder: 0
  });
  const [showEditUser, setShowEditUser] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editUser, setEditUser] = useState({
    name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    address: '',
    role: 'user' as 'user' | 'professional' | 'admin',
    emergency_contact: {
      name: '',
      phone: '',
      relationship: ''
    }
  });
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [changingPasswordFor, setChangingPasswordFor] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCreateService, setShowCreateService] = useState(false);
  const [newService, setNewService] = useState({
    name: '',
    description: '',
    price: 0,
    currency: 'ARS',
    duration_minutes: 30,
    active: true
  });
  const [showEditService, setShowEditService] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [editService, setEditService] = useState({
    name: '',
    description: '',
    price: 0,
    currency: 'ARS',
    duration_minutes: 30,
    active: true
  });
  const [showCreateRoom, setShowCreateRoom] = useState(false);
  const [newRoom, setNewRoom] = useState({
    name: '',
    description: '',
    is_active: true,
    sort_order: 0,
    hourly_rate: 0
  });
  const [showEditRoom, setShowEditRoom] = useState(false);
  const [editingRoom, setEditingRoom] = useState<Room | null>(null);
  const [editRoom, setEditRoom] = useState({
    name: '',
    description: '',
    is_active: true,
    sort_order: 0,
    hourly_rate: 0
  });
  const [draggedProfessional, setDraggedProfessional] = useState<string | null>(null);
  const [isReordering, setIsReordering] = useState(false);
  
  // Delete confirmation modal state
  const [showDeleteProfessionalModal, setShowDeleteProfessionalModal] = useState(false);
  const [professionalToDelete, setProfessionalToDelete] = useState<Professional | null>(null);
  const [deletingProfessional, setDeletingProfessional] = useState(false);
  
  // Activity logs state
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [activityLogsLoading, setActivityLogsLoading] = useState(false);
  const [selectedProfessionalForActivity, setSelectedProfessionalForActivity] = useState<string>('');
  const [activityFilter, setActivityFilter] = useState({
    action: '',
    month: '',
    year: ''
  });
  const [activityPagination, setActivityPagination] = useState({
    total: 0,
    page: 1,
    limit: 50,
    pages: 0
  });


  
  // New state for enhanced room availability management
  const [showMonthlyRoomAvailabilityModal, setShowMonthlyRoomAvailabilityModal] = useState(false);
  const [selectedRoomForMonthlyAvailability, setSelectedRoomForMonthlyAvailability] = useState<Room | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<RoomTemplate | null>(null);
  // Template builder state is handled by the RoomTemplateBuilder component

  // Generate default templates using the new system
  const defaultTemplates = generateDefaultTemplates();

  // Initialize with default template
  useEffect(() => {
    if (!selectedTemplate && Object.keys(defaultTemplates).length > 0) {
      setSelectedTemplate(defaultTemplates['business-hours']);
    }
  }, [defaultTemplates, selectedTemplate]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    // things that need the admin's attention; failures here must never block the dashboard
    api.get('/admin/attention').then(r => setAttention(r.data)).catch(() => undefined);
    try {
      setLoading(true);
      const [professionalsRes, usersRes, appointmentsRes, roomsRes, configRes, servicesRes, timezoneRes, specialtiesRes] = await Promise.all([
        api.get('/admin/professionals'),
        api.get('/admin/users'),
        api.get('/admin/appointments'),
        api.get('/admin/rooms'),
        api.get('/admin/config/room-rental'),
        api.get('/admin/services'),
        api.get('/admin/config/timezone'),
        api.get('/admin/specialties')
      ]);
      setProfessionals(professionalsRes.data);
      setUsers(usersRes.data);
      setFilteredUsers(usersRes.data);
      setAppointments(appointmentsRes.data || []);
      setRooms(roomsRes.data || []);
      setSystemConfig(configRes.data);
      setServices(servicesRes.data || []);
      setSpecialties(specialtiesRes.data || []);
      setRateSteps(configRes.data?.roomRental?.rates || []);
      if (timezoneRes.data) {
        setTimezoneConfig(timezoneRes.data);
      }
    } catch (error) {
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };


  const handleSaveTimezoneConfig = async () => {
    try {
      await api.put('/admin/config/timezone', timezoneConfig);
      toast.success('Configuración de zona horaria actualizada');
      // Refresh the configuration
      const timezoneRes = await api.get('/admin/config/timezone');
      if (timezoneRes.data) {
        setTimezoneConfig(timezoneRes.data);
      }
    } catch (error) {
      toast.error('No se pudo actualizar la configuración de zona horaria');
    }
  };

  

  const handleUserSearch = (searchTerm: string) => {
    setUserSearchTerm(searchTerm);
    if (searchTerm.trim() === '') {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(user =>
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.phone && user.phone.includes(searchTerm))
      );
      setFilteredUsers(filtered);
    }
  };




  const handleCreateProfessional = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/admin/professionals', newProfessional);
      toast.success('Profesional creado exitosamente');
      setShowCreateProfessional(false);
      setNewProfessional({
        email: '',
        password: '',
        name: '',
        specialty_id: 0,
        specialty_ids: [],
        phone: '',
        bio: '',
        sortOrder: 0
      });
      fetchData(); // Refresh data
    } catch (error: any) {
      toast.error(error.response?.data?.error || error.response?.data?.errors?.[0]?.msg || 'No pudimos crear el profesional', { duration: 7000 });
    }
  };







  const handleEditProfessional = (professional: Professional) => {
    setEditingProfessional(professional);
    setEditProfessional({
      name: professional.user?.name || '',
      email: professional.user?.email || '',
      specialty_id: professional.specialtyId || 0,
      specialty_ids: (professional.specialties || []).map(sp => sp.id),
      bio: professional.bio || '',
      phone: professional.user?.phone || '',
      sortOrder: professional.sortOrder || 0
    });
    setShowEditProfessional(true);
  };

  const handleDeleteProfessional = async (professionalId: string) => {
    // Find the professional to get their details for the modal
    const professional = professionals.find(p => p.id === professionalId);
    if (professional) {
      setProfessionalToDelete(professional);
      setShowDeleteProfessionalModal(true);
    }
  };

  const confirmDeleteProfessional = async () => {
    if (!professionalToDelete) return;
    
    setDeletingProfessional(true);
    try {
      const response = await api.delete(`/admin/professionals/${professionalToDelete.id}`);
      
      // Show detailed success message with what was deleted
      if (response.data && response.data.relatedDataDeleted) {
        const { summary } = response.data;
        toast.success(
          `Profesional eliminado exitosamente. ${summary}`,
          { duration: 6000 }
        );
        
        // Show additional details in console for debugging
        console.log('Professional deletion details:', response.data);
      } else {
        toast.success('Profesional eliminado exitosamente');
      }
      
      // Close modal and refresh data
      setShowDeleteProfessionalModal(false);
      setProfessionalToDelete(null);
      fetchData();
    } catch (error: any) {
      console.error('Error deleting professional:', error);
      const errorMessage = error.response?.data?.error || 'Error al eliminar el profesional';
      toast.error(errorMessage);
    } finally {
      setDeletingProfessional(false);
    }
  };

  const cancelDeleteProfessional = () => {
    setShowDeleteProfessionalModal(false);
    setProfessionalToDelete(null);
    setDeletingProfessional(false);
  };

  const handleUpdateProfessional = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingProfessional) return;
    
    try {
      await api.put(`/admin/professionals/${editingProfessional.id}`, editProfessional);
      toast.success('Profesional actualizado exitosamente');
      setShowEditProfessional(false);
      setEditingProfessional(null);
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'No pudimos actualizar el profesional', { duration: 7000 });
    }
  };


  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setEditUser({
      name: user.name,
      email: user.email,
      phone: user.phone || '',
      date_of_birth: user.date_of_birth ? convertISOToArgentine(user.date_of_birth.split('T')[0]) : '',
      address: user.address || '',
      role: user.role,
      emergency_contact: user.emergency_contact || {
        name: '',
        phone: '',
        relationship: ''
      }
    });
    setShowEditUser(true);
  };

  const handleDeleteUser = async (userId: string) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este usuario? Esta acción no se puede deshacer.')) {
      try {
        await api.delete(`/admin/users/${userId}`);
        toast.success('Usuario eliminado exitosamente');
        fetchData();
      } catch (error) {
        toast.error('Error al eliminar el usuario');
      }
    }
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;
    
    try {
      // Convert Argentine date format back to ISO for the API
      const updateData = {
        ...editUser,
        date_of_birth: editUser.date_of_birth ? convertArgentineToISO(editUser.date_of_birth) : ''
      };
      
      await api.put(`/admin/users/${editingUser.id}`, updateData);
      toast.success('Usuario actualizado exitosamente');
      setShowEditUser(false);
      setEditingUser(null);
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar el usuario');
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!changingPasswordFor) return;
    
    if (newPassword !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    try {
      await api.put(`/admin/users/${changingPasswordFor.id}/password`, {
        newPassword
      });
      toast.success('Contraseña actualizada exitosamente');
      setShowChangePassword(false);
      setChangingPasswordFor(null);
      setNewPassword('');
      setConfirmPassword('');
      // Return to edit user modal if we came from there
      if (editingUser) {
        setShowEditUser(true);
      }
    } catch (error) {
      toast.error('Error al actualizar la contraseña');
    }
  };

  const handleCreateService = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/admin/services', newService);
      toast.success('Servicio creado exitosamente');
      setShowCreateService(false);
      setNewService({
        name: '',
        description: '',
        price: 0,
        currency: 'ARS',
        duration_minutes: 30,
        active: true
      });
      fetchData();
    } catch (error) {
      toast.error('Error al crear el servicio');
    }
  };

  const handleEditService = (service: Service) => {
    setEditingService(service);
    setEditService({
      name: service.name,
      description: service.description || '',
      price: service.price,
      currency: service.currency,
      duration_minutes: service.duration_minutes,
      active: service.active
    });
    setShowEditService(true);
  };

  const handleDeleteService = async (serviceId: string) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este servicio? Esta acción no se puede deshacer.')) {
      try {
        await api.delete(`/admin/services/${serviceId}`);
        toast.success('Servicio eliminado exitosamente');
        fetchData();
      } catch (error) {
        toast.error('Error al eliminar el servicio');
      }
    }
  };

  const handleUpdateService = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingService) return;
    
    try {
      await api.put(`/admin/services/${editingService.id}`, editService);
      toast.success('Servicio actualizado exitosamente');
      setShowEditService(false);
      setEditingService(null);
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar el servicio');
    }
  };

  // Room management functions
  const handleCreateRoom = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/admin/rooms', newRoom);
      toast.success('Sala creada exitosamente');
      setShowCreateRoom(false);
      setNewRoom({
        name: '',
        description: '',
        is_active: true,
        sort_order: 0,
        hourly_rate: 0
      });
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al crear la sala');
    }
  };

  const handleEditRoom = (room: Room) => {
    setEditingRoom(room);
    setEditRoom({
      name: room.name,
      description: room.description || '',
      is_active: room.is_active,
      sort_order: room.sort_order,
      hourly_rate: room.hourly_rate || 0
    });
    setShowEditRoom(true);
  };

  const handleUpdateRoom = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingRoom) return;
    
    try {
      await api.put(`/admin/rooms/${editingRoom.id}`, editRoom);
      toast.success('Sala actualizada exitosamente');
      setShowEditRoom(false);
      setEditingRoom(null);
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al actualizar la sala');
    }
  };

  const handleDeleteRoom = async (roomId: string) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta sala?')) {
      return;
    }
    
    try {
      await api.delete(`/admin/rooms/${roomId}`);
      toast.success('Sala eliminada exitosamente');
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al eliminar la sala');
    }
  };



  const handleManageMonthlyRoomAvailability = (room: Room) => {
    setSelectedRoomForMonthlyAvailability(room);
    setShowMonthlyRoomAvailabilityModal(true);
  };





  // Configuration management functions
  const saveRates = async (rates: Array<{ from: string; hourly: number }>) => {
    try {
      const response = await api.put('/config/room-rental', { rates });
      setSystemConfig(response.data);
      setRateSteps(response.data?.roomRental?.rates || []);
      toast.success('Precio por hora actualizado');
      return true;
    } catch (error: any) {
      toast.error(error.response?.data?.error || error.response?.data?.errors?.[0]?.msg || 'No pudimos guardar el precio');
      return false;
    }
  };
  const addRateStep = async () => {
    const hourly = Number(newStep.hourly);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(newStep.from) || !(hourly > 0)) { toast.error('Elegí desde qué fecha rige y el precio por hora'); return; }
    if (await saveRates([...rateSteps.filter(r => r.from !== newStep.from), { from: newStep.from, hourly }])) setNewStep({ from: '', hourly: '' });
  };
  const removeRateStep = (from: string) => {
    if (rateSteps.length <= 1) { toast.error('Tiene que quedar al menos un precio'); return; }
    if (window.confirm('¿Quitar este precio? Los alquileres de esas fechas pasan a calcularse con el precio anterior.')) saveRates(rateSteps.filter(r => r.from !== from));
  };
  const money = (n: number) => `$${new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 }).format(n)}`;
  const today = new Date().toISOString().slice(0, 10);
  const currentStep = [...rateSteps].filter(r => r.from <= today).pop();


  const handleCreateDeductionRule = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await api.post('/admin/config/room-rental/deduction-rules', newDeductionRule);
      setSystemConfig(response.data);
      setShowCreateDeductionRule(false);
      setNewDeductionRule({ minHoursPerDay: 0, discountPercentage: 0, description: '' });
      toast.success('Regla de descuento agregada exitosamente');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al agregar la regla de descuento');
    }
  };

  const handleEditDeductionRule = (rule: { id?: string; minHoursPerDay: number; discountPercentage: number; description: string }) => {
    if (rule.id) {
      setEditingDeductionRuleId(rule.id);
      setNewDeductionRule({
        minHoursPerDay: rule.minHoursPerDay,
        discountPercentage: rule.discountPercentage,
        description: rule.description
      });
      setShowEditDeductionRule(true);
    } else {
      toast.error('Esta regla no tiene un ID válido y no se puede editar');
    }
  };

  const handleUpdateDeductionRule = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingDeductionRuleId) return;

    try {
      const response = await api.put(`/admin/config/room-rental/deduction-rules/${editingDeductionRuleId}`, newDeductionRule);
      setSystemConfig(response.data);
      setShowEditDeductionRule(false);
      setEditingDeductionRuleId(null);
      setNewDeductionRule({ minHoursPerDay: 0, discountPercentage: 0, description: '' });
      toast.success('Regla de descuento actualizada exitosamente');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al actualizar la regla de descuento');
    }
  };



  const handleDeleteDeductionRule = async (id: string) => {
    if (!id) {
      toast.error('Esta regla no tiene un ID válido y no se puede eliminar');
      return;
    }

    if (!window.confirm('¿Estás seguro de que quieres eliminar esta regla de descuento?')) {
      return;
    }
    
    try {
      const response = await api.delete(`/admin/config/room-rental/deduction-rules/${id}`);
      setSystemConfig(response.data);
      toast.success('Regla de descuento eliminada exitosamente');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al eliminar la regla de descuento');
    }
  };



  // Drag and drop handlers for professional reordering
  const handleDragStart = (e: React.DragEvent, professionalId: string) => {
    setDraggedProfessional(professionalId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, targetProfessionalId: string) => {
    e.preventDefault();
    if (!draggedProfessional || draggedProfessional === targetProfessionalId) {
      setDraggedProfessional(null);
      return;
    }

    try {
      setIsReordering(true);
      
      // Create new order array
      const currentOrder = professionals.map(p => p.id);
      const draggedIndex = currentOrder.indexOf(draggedProfessional);
      const targetIndex = currentOrder.indexOf(targetProfessionalId);
      
      // Remove dragged item and insert at target position
      const newOrder = [...currentOrder];
      newOrder.splice(draggedIndex, 1);
      newOrder.splice(targetIndex, 0, draggedProfessional);
      
      // Send new order to backend
      await api.put('/admin/professionals/reorder', {
        professionalIds: newOrder
      });
      
      toast.success('Orden de profesionales actualizado');
      fetchData();
    } catch (error) {
      toast.error('Error al reordenar profesionales');
    } finally {
      setDraggedProfessional(null);
      setIsReordering(false);
    }
  };

  const handleDragEnd = () => {
    setDraggedProfessional(null);
  };



  const fetchActivityLogsWithFilter = useCallback(async (page = 1, action = '', month = '', year = '') => {
    try {
      setActivityLogsLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50',
        ...(selectedProfessionalForActivity && { professional_id: selectedProfessionalForActivity }),
        ...(action && { action: action }),
        ...(month && { month: month }),
        ...(year && { year: year })
      });

      console.log('🔍 Fetching activity logs with explicit filters:', {
        professional_id: selectedProfessionalForActivity,
        action: action,
        month: month,
        year: year,
        url: `/admin/activity-logs?${params}`
      });

      const response = await api.get(`/admin/activity-logs?${params}`);
      
      console.log('📊 Activity logs response:', {
        totalLogs: response.data.logs.length,
        logs: response.data.logs,
        pagination: response.data.pagination
      });
      
      setActivityLogs(response.data.logs);
      setActivityPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching activity logs:', error);
      toast.error('Error al cargar los logs de actividad');
    } finally {
      setActivityLogsLoading(false);
    }
  }, [selectedProfessionalForActivity]);

  const fetchActivityLogs = useCallback(async (page = 1) => {
    // Use the current filter state
    return fetchActivityLogsWithFilter(page, activityFilter.action, activityFilter.month, activityFilter.year);
  }, [activityFilter, fetchActivityLogsWithFilter]);

  useEffect(() => {
    if (activeTab === 'reports' && activeSubTab === 'activity') {
      // When switching to activity sub-tab, fetch logs with current filters
      fetchActivityLogs(1);
    }
  }, [activeTab, activeSubTab, fetchActivityLogs]);



  const getActionLabel = (action: string) => {
    switch (action) {
      case 'availability_updated': return 'Disponibilidad Mensual Actualizada';
      case 'template_applied': return 'Plantilla Semanal Aplicada';
      case 'day_override': return 'Día Específico Modificado';
      case 'rate_updated': return 'Tarifa de Turno Actualizada';
      case 'profile_updated': return 'Perfil Profesional Actualizado';
      default: return action;
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'availability_updated': return 'bg-blue-100 text-blue-800';
      case 'template_applied': return 'bg-green-100 text-green-800';
      case 'day_override': return 'bg-yellow-100 text-yellow-800';
      case 'rate_updated': return 'bg-purple-100 text-purple-800';
      case 'profile_updated': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <motion.div
      className="min-h-screen bg-gray-50"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      <PageBand title="Administración" subtitle="Profesionales, turnos, usuarios y configuración del sistema." />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Needs attention: a room with no schedule can't be rented and offers no slots to patients */}
        {attention.roomGaps.length > 0 && (
          <div role="alert" className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
            <p className="font-semibold">Faltan horarios de consultorios</p>
            <ul className="mt-1 list-disc pl-5">
              {attention.roomGaps.map(g => (
                <li key={`${g.roomId}-${g.year}-${g.month}`}>{g.roomName} no tiene horarios para {String(g.month).padStart(2, '0')}/{g.year}: nadie puede alquilarlo ni reservar turnos ahí.</li>
              ))}
            </ul>
            <button className="mt-2 font-semibold underline" onClick={() => { setActiveTab('system'); setActiveSubTab('rooms'); }}>Configurar horarios</button>
          </div>
        )}

        {/* Main Navigation Tabs */}
        <motion.div
          className="flex space-x-1 bg-white p-1.5 rounded-xl shadow-sm border border-gray-200 mb-4 overflow-x-auto whitespace-nowrap"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: 'easeOut', delay: 0.08 }}
        >
          <button
            onClick={() => { setActiveTab('professionals'); setActiveSubTab(''); }}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
              activeTab === 'professionals'
                ? 'bg-primary-600 text-white'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            } ripple`}
          >
            <Users className="h-4 w-4" />
            <span>Profesionales</span>
          </button>
          
          <button
            onClick={() => { setActiveTab('appointments'); setActiveSubTab('calendar'); }}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
              activeTab === 'appointments'
                ? 'bg-primary-600 text-white'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            } ripple`}
          >
            <Calendar className="h-4 w-4" />
            <span>Turnos</span>
            {appointments.filter((a: any) => a.status === 'pending').length > 0 && (
              <span className="ml-1 inline-flex min-w-[1.25rem] h-5 items-center justify-center rounded-full bg-accent-gold px-1.5 text-xs font-bold text-primary-900" title="Turnos pendientes de respuesta">
                {appointments.filter((a: any) => a.status === 'pending').length}
              </span>
            )}
          </button>
          
          <button
            onClick={() => { setActiveTab('users'); setActiveSubTab(''); }}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
              activeTab === 'users'
                ? 'bg-primary-600 text-white'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            } ripple`}
          >
            <UserIcon className="h-4 w-4" />
            <span>Usuarios</span>
          </button>
          
          <button
            onClick={() => { setActiveTab('system'); setActiveSubTab('specialties'); }}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
              activeTab === 'system'
                ? 'bg-primary-600 text-white'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            } ripple`}
          >
            <Settings className="h-4 w-4" />
            <span>Sistema</span>
          </button>
          
          <button
            onClick={() => { setActiveTab('reports'); setActiveSubTab('activity'); }}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
              activeTab === 'reports'
                ? 'bg-primary-600 text-white'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            } ripple`}
          >
            <Clock className="h-4 w-4" />
            <span>Reportes</span>
          </button>
        </motion.div>

        {/* Sub Navigation */}
        {(activeTab === 'appointments' || activeTab === 'system' || activeTab === 'reports') && (
          <motion.div 
            className="flex space-x-1 bg-gray-100 p-1 rounded-xl mb-6 overflow-x-auto whitespace-nowrap"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            {activeTab === 'appointments' && (
              <>
                <button
                  onClick={() => setActiveSubTab('calendar')}
                  className={`flex items-center space-x-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
                    activeSubTab === 'calendar'
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <Calendar className="h-3 w-3" />
                  <span>Calendario</span>
                </button>
                <button
                  onClick={() => setActiveSubTab('agenda')}
                  className={`flex items-center space-x-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
                    activeSubTab === 'agenda'
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <Users className="h-3 w-3" />
                  <span>Agenda por profesional</span>
                </button>
                <button
                  onClick={() => setActiveSubTab('requests')}
                  className={`flex items-center space-x-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
                    activeSubTab === 'requests'
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <Calendar className="h-3 w-3" />
                  <span>Solicitudes</span>
                  {attention.pendingRequests > 0 && <span className="ml-1 inline-flex min-w-[1.25rem] h-5 items-center justify-center rounded-full bg-accent-gold px-1.5 text-xs font-bold text-primary-900" title="Solicitudes sin horario esperando respuesta">{attention.pendingRequests}</span>}
                </button>
                <button
                  onClick={() => setActiveSubTab('availability')}
                  className={`flex items-center space-x-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
                    activeSubTab === 'availability'
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <ClockIcon className="h-3 w-3" />
                  <span>Ocupación de salas</span>
                </button>
              </>
            )}
            
            {activeTab === 'system' && (
              <>
                <button
                  onClick={() => setActiveSubTab('specialties')}
                  className={`flex items-center space-x-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
                    activeSubTab === 'specialties'
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <Settings className="h-3 w-3" />
                  <span>Especialidades</span>
                </button>
                <button
                  onClick={() => setActiveSubTab('rooms')}
                  className={`flex items-center space-x-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
                    activeSubTab === 'rooms'
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <Building className="h-3 w-3" />
                  <span>Salas</span>
                </button>
                <button
                  onClick={() => setActiveSubTab('configuration')}
                  className={`flex items-center space-x-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
                    activeSubTab === 'configuration'
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <Settings className="h-3 w-3" />
                  <span>Configuración</span>
                </button>
              </>
            )}
            
            {activeTab === 'reports' && (
              <>
                <button
                  onClick={() => setActiveSubTab('activity')}
                  className={`flex items-center space-x-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
                    activeSubTab === 'activity'
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <Clock className="h-3 w-3" />
                  <span>Actividad</span>
                </button>
                <button
                  onClick={() => setActiveSubTab('payments')}
                  className={`flex items-center space-x-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
                    activeSubTab === 'payments'
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <Building className="h-3 w-3" />
                  <span>Alquileres y cobros</span>
                </button>
              </>
            )}
          </motion.div>
        )}



        {/* Professionals Tab */}
        {activeTab === 'professionals' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Profesionales</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Arrastra y suelta las filas para cambiar el orden de aparición en la página principal
                </p>
              </div>
              <button
                onClick={() => setShowCreateProfessional(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <UserPlus className="h-4 w-4" />
                <span>Agregar Profesional</span>
              </button>
            </div>

            <div className="bg-white rounded-lg shadow">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Orden
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Nombre
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Especialidad
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Correo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Teléfono
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {professionals.map((professional) => (
                      <tr 
                        key={professional.id} 
                        className={`hover:bg-gray-50 cursor-move ${
                          draggedProfessional === professional.id ? 'opacity-50' : ''
                        } ${isReordering ? 'pointer-events-none' : ''}`}
                        draggable={!isReordering}
                        onDragStart={(e) => handleDragStart(e, professional.id)}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, professional.id)}
                        onDragEnd={handleDragEnd}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500 font-mono">{(professional.sortOrder || 0) + 1}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{professional.user?.name || 'Desconocido'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{professional.specialties?.length ? professional.specialties.map(sp => sp.name).join(" · ") : professional.specialty}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{professional.user?.email || 'Desconocido'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{professional.user?.phone || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => handleEditProfessional(professional)}
                              className="text-primary-600 hover:text-primary-900 flex items-center space-x-1"
                            >
                              <Edit className="h-4 w-4" />
                              <span>Editar</span>
                            </button>
                            <button
                              onClick={() => handleDeleteProfessional(professional.id)}
                              className="text-red-600 hover:text-red-900 flex items-center space-x-1"
                            >
                              <Trash2 className="h-4 w-4" />
                              <span>Eliminar</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Appointments - Calendar Tab */}
        {activeTab === 'appointments' && activeSubTab === 'calendar' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Calendario de Turnos</h2>
                <p className="text-sm text-gray-500 mt-1">Vista general de todos los turnos del centro</p>
              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setActiveSubTab('agenda')}
                  className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <Calendar className="h-4 w-4" />
                  <span>Agendar un turno</span>
                </button>
              </div>
            </div>
            {attention.pendingRequests > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                <span><b>{attention.pendingRequests}</b> solicitud{attention.pendingRequests !== 1 ? 'es' : ''} de pacientes sin horario esperan respuesta.</span>
                <button className="font-semibold underline" onClick={() => setActiveSubTab('requests')}>Ver solicitudes</button>
              </div>
            )}
            <ImprovedAppointmentCalendar 
              appointments={appointments} 
              isAdmin={true}
              showCreateButton={false}
              professionals={professionals}
              onStatusUpdate={async (appointmentId: string, status: string) => {
                // sendEmail can fail silently on the server; the API now tells us, so we never claim "email sent" falsely
                const emailNote = (sent: boolean, ok: string) => sent
                  ? toast.success(`${ok} y se avisó al paciente por email`)
                  : toast(`${ok}, pero NO pudimos enviar el email. Avisale al paciente por teléfono o WhatsApp.`, { icon: '⚠️', duration: 9000 });
                try {
                  if (status === 'confirmed') {
                    const r = await api.put(`/admin/appointments/${appointmentId}/confirm`);
                    emailNote(!!r.data?.emailSent, 'Turno confirmado');
                  } else if (status === 'cancelled') {
                    const r = await api.put(`/admin/appointments/${appointmentId}/cancel`);
                    if (r.data?.alreadyCancelled) toast('Ese turno ya estaba cancelado');
                    else emailNote(!!r.data?.emailSent, 'Turno cancelado');
                  } else {
                    await api.put(`/appointments/${appointmentId}/status`, { status });
                    toast.success('Estado del turno actualizado');
                  }
                  fetchData(); // Refresh data
                } catch (error: any) {
                  console.error('Error updating appointment status:', error);
                  toast.error(error.response?.data?.error || 'No se pudo actualizar el turno');
                  fetchData(); // the list may be stale (e.g. someone else already handled it)
                }
              }}
            />
          </div>
        )}


        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Gestionar Usuarios</h2>
                <p className="text-sm text-gray-500 mt-1">Total de usuarios registrados: {filteredUsers.length}</p>
              </div>
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Buscar usuarios..."
                    value={userSearchTerm}
                    onChange={(e) => handleUserSearch(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  <UserIcon className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Nombre
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Correo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rol
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Teléfono
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Turnos
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fecha de Registro
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredUsers.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-6 py-12 text-center">
                          <div className="text-gray-500">
                            {userSearchTerm ? 'No se encontraron usuarios que coincidan con la búsqueda.' : 'No hay usuarios registrados.'}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredUsers.map((user) => (
                        <tr key={user.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{user.name}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{user.email}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              user.role === 'admin' ? 'bg-red-100 text-red-800' :
                              user.role === 'professional' ? 'bg-blue-100 text-blue-800' :
                              'bg-green-100 text-green-800'
                            }`}>
                              {user.role === 'admin' ? 'Administrador' :
                               user.role === 'professional' ? 'Profesional' :
                               'Paciente'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{user.phone || 'N/A'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <span className="text-sm text-gray-900">
                                {user.appointments?.length || 0} turnos
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {new Date(user.createdAt).toLocaleDateString('es-AR')}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                onClick={() => handleEditUser(user)}
                                className="text-primary-600 hover:text-primary-900 flex items-center space-x-1"
                              >
                                <Edit className="h-4 w-4" />
                                <span>Editar</span>
                              </button>
                              <button
                                onClick={() => handleDeleteUser(user.id)}
                                className="text-red-600 hover:text-red-900 flex items-center space-x-1"
                              >
                                <Trash2 className="h-4 w-4" />
                                <span>Eliminar</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}





        {/* System - Configuration Tab */}
        {activeTab === 'system' && activeSubTab === 'configuration' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Configuración del Sistema</h2>
                <p className="text-sm text-gray-500 mt-1">Gestionar configuraciones, servicios y salas</p>
              </div>
            </div>

            {/* Room Rental Configuration */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Configuración de Alquiler de Salas</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Precio por hora (ARS)</label>
                  <p className="mb-3 text-sm text-gray-500">Cada precio rige desde su fecha hasta que empieza el siguiente. {currentStep && <>Hoy: <b>{money(currentStep.hourly)}</b> por hora.</>}</p>
                  <ul className="mb-3 divide-y divide-gray-100 rounded-md border border-gray-200">
                    {rateSteps.map(r => (
                      <li key={r.from} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                        <span>{r.from <= '2000-01-01' ? 'Precio inicial' : `Desde ${r.from.split('-').reverse().join('/')}`}: <b>{money(r.hourly)}</b> por hora{r === currentStep && <span className="ml-2 rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-800">vigente</span>}</span>
                        <button type="button" onClick={() => removeRateStep(r.from)} className="text-red-600 hover:text-red-900">Quitar</button>
                      </li>
                    ))}
                    {rateSteps.length === 0 && <li className="px-3 py-2 text-sm text-gray-500">Todavía no hay precios cargados.</li>}
                  </ul>
                  <div className="flex flex-wrap gap-2">
                    <input type="date" aria-label="Rige desde" value={newStep.from} onChange={e => setNewStep(n => ({ ...n, from: e.target.value }))}
                      className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500" />
                    <input type="number" min="0" step="100" placeholder="Precio por hora" aria-label="Precio por hora" value={newStep.hourly} onChange={e => setNewStep(n => ({ ...n, hourly: e.target.value }))}
                      className="w-40 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500" />
                    <button type="button" onClick={addRateStep} className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors">Agregar precio</button>
                  </div>
                </div>
              </div>

              {/* Deduction Rules */}
              <div className="mt-6">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-md font-medium text-gray-900">Reglas de Descuento</h4>
                  <button
                    onClick={() => setShowCreateDeductionRule(true)}
                    className="px-3 py-1 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors text-sm"
                  >
                    Agregar Regla
                  </button>
                </div>

                <div className="space-y-3">
                  {systemConfig?.roomRental.deductionRules.map((rule, index) => (
                    <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-md">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">
                          {rule.minHoursPerDay}+ horas: {rule.discountPercentage}% descuento
                        </div>
                        <div className="text-sm text-gray-500">{rule.description}</div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEditDeductionRule(rule)}
                          className="text-primary-600 hover:text-primary-900 text-sm"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleDeleteDeductionRule(rule.id)}
                          className="text-red-600 hover:text-red-900 text-sm"
                        >
                          Eliminar
                        </button>
                      </div>
                    </div>
                  ))}
                  
                  {(!systemConfig?.roomRental.deductionRules || systemConfig.roomRental.deductionRules.length === 0) && (
                    <div className="text-center py-4 text-gray-500">
                      No hay reglas de descuento configuradas
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Timezone Configuration */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Configuración de Zona Horaria</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Zona Horaria
                  </label>
                  <select
                    value={timezoneConfig.timezone}
                    onChange={(e) => setTimezoneConfig({...timezoneConfig, timezone: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="America/Argentina/Buenos_Aires">Argentina (Buenos Aires)</option>
                    <option value="America/New_York">Eastern Time (US)</option>
                    <option value="America/Chicago">Central Time (US)</option>
                    <option value="America/Denver">Mountain Time (US)</option>
                    <option value="America/Los_Angeles">Pacific Time (US)</option>
                    <option value="Europe/London">London (GMT)</option>
                    <option value="Europe/Paris">Paris (CET)</option>
                    <option value="Asia/Tokyo">Tokyo (JST)</option>
                    <option value="Australia/Sydney">Sydney (AEST)</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Idioma
                  </label>
                  <select
                    value={timezoneConfig.locale}
                    onChange={(e) => setTimezoneConfig({...timezoneConfig, locale: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="es-AR">Español (Argentina)</option>
                    <option value="en-US">English (US)</option>
                    <option value="en-GB">English (UK)</option>
                    <option value="pt-BR">Português (Brasil)</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Moneda
                  </label>
                  <select
                    value={timezoneConfig.currency}
                    onChange={(e) => setTimezoneConfig({...timezoneConfig, currency: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="ARS">Peso Argentino (ARS)</option>
                    <option value="USD">US Dollar (USD)</option>
                    <option value="EUR">Euro (EUR)</option>
                    <option value="BRL">Real Brasileiro (BRL)</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Código de País
                  </label>
                  <input
                    type="text"
                    value={timezoneConfig.country_code}
                    onChange={(e) => setTimezoneConfig({...timezoneConfig, country_code: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="+54"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hora de Inicio por Defecto
                  </label>
                  <input
                    type="time"
                    value={timezoneConfig.default_start_time}
                    onChange={(e) => setTimezoneConfig({...timezoneConfig, default_start_time: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hora de Fin por Defecto
                  </label>
                  <input
                    type="time"
                    value={timezoneConfig.default_end_time}
                    onChange={(e) => setTimezoneConfig({...timezoneConfig, default_end_time: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
              
              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleSaveTimezoneConfig}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                >
                  Guardar Configuración
                </button>
              </div>
            </div>

            {/* Services Management */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Servicios</h3>
                <button
                  onClick={() => setShowCreateService(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <ClockIcon className="h-4 w-4" />
                  <span>Agregar Servicio</span>
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Nombre
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Descripción
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Precio
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Duración
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Estado
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {services.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-12 text-center">
                          <div className="text-gray-500">
                            No hay servicios configurados.
                          </div>
                        </td>
                      </tr>
                    ) : (
                      services.map((service) => (
                        <tr key={service.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{service.name}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{service.description || 'N/A'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {formatCurrency(service.price, service.currency)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{service.duration_minutes} min</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              service.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {service.active ? 'Activo' : 'Inactivo'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                onClick={() => handleEditService(service)}
                                className="text-primary-600 hover:text-primary-900 flex items-center space-x-1"
                              >
                                <Edit className="h-4 w-4" />
                                <span>Editar</span>
                              </button>
                              <button
                                onClick={() => handleDeleteService(service.id)}
                                className="text-red-600 hover:text-red-900 flex items-center space-x-1"
                              >
                                <Trash2 className="h-4 w-4" />
                                <span>Eliminar</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        )}

        {/* System - Rooms Tab */}
        {activeTab === 'system' && activeSubTab === 'rooms' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Gestión de Salas</h2>
                <p className="text-sm text-gray-500 mt-1">Configurar salas y sus horarios de disponibilidad</p>
              </div>
              <button
                onClick={() => setShowCreateRoom(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <Building className="h-4 w-4" />
                <span>Agregar Sala</span>
              </button>
            </div>

            {/* Room Management */}
            <div className="bg-white rounded-lg shadow">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Nombre
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Descripción
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Estado
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Orden
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {rooms.map((room) => (
                      <tr key={room.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{room.name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{room.description || '-'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            room.is_active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {room.is_active ? 'Activa' : 'Inactiva'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {room.sort_order}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleEditRoom(room)}
                              className="text-primary-600 hover:text-primary-900"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteRoom(room.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Room Availability Management */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Gestión de Disponibilidad de Salas</h3>
              <p className="text-sm text-gray-500 mb-4">
                Configura los horarios de disponibilidad para cada sala. Los profesionales solo podrán 
                programar citas cuando las salas estén disponibles.
              </p>
              
              {/* Responsive grid for rooms - 2 columns on large screens, 1 on small */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                {rooms.map((room) => (
                  <div key={room.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">{room.name}</h4>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        room.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {room.is_active ? 'Activa' : 'Inactiva'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mb-3">{room.description || 'Sin descripción'}</p>
                    <div className="space-y-2">
                      <button
                        onClick={() => handleManageMonthlyRoomAvailability(room)}
                        className="w-full px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors text-sm"
                      >
                        Gestionar Disponibilidad
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}


        {/* Reports - Activity Tab */}
        {activeTab === 'reports' && activeSubTab === 'activity' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Actividad de Profesionales</h2>
                <p className="text-sm text-gray-500 mt-1">Seguimiento de cambios en disponibilidad y configuraciones</p>
              </div>
              <button
                onClick={() => fetchActivityLogs(1)}
                className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <Clock className="h-4 w-4" />
                <span>Actualizar</span>
              </button>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow p-6">
              {/* Debug Info */}
              <div className="mb-4 p-3 bg-gray-100 rounded-md text-sm">
                <div className="font-medium text-gray-700 mb-2">Debug Info - Current Filters:</div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>Professional ID: <span className="font-mono">{selectedProfessionalForActivity || 'None'}</span></div>
                  <div>Action: <span className="font-mono">{activityFilter.action || 'None'}</span></div>
                  <div>Month: <span className="font-mono">{activityFilter.month || 'None'}</span></div>
                  <div>Year: <span className="font-mono">{activityFilter.year || 'None'}</span></div>
                </div>
                <div className="mt-2">Total Logs Displayed: <span className="font-mono">{activityLogs.length}</span></div>
                <div className="mt-2">Actions in Logs: <span className="font-mono">
                  {activityLogs.map(log => log.action).join(', ') || 'None'}
                </span></div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Profesional</label>
                  <select
                    value={selectedProfessionalForActivity}
                    onChange={(e) => {
                      setSelectedProfessionalForActivity(e.target.value);
                      setActivityPagination(prev => ({ ...prev, page: 1 }));
                      setTimeout(() => {
                        fetchActivityLogs(1);
                      }, 0);
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Todos los profesionales</option>
                    {professionals.map((professional) => (
                      <option key={professional.id} value={professional.id}>
                        {professional.user?.name || 'Desconocido'}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Acción</label>
                  <select
                    value={activityFilter.action}
                    onChange={(e) => {
                      console.log('🎯 Action filter changed to:', e.target.value);
                      const newAction = e.target.value;
                      setActivityFilter(prev => ({ ...prev, action: newAction }));
                      // Use the new value directly instead of relying on state
                      setActivityPagination(prev => ({ ...prev, page: 1 }));
                      // Call the function with the new action value
                      setTimeout(() => {
                        fetchActivityLogs(1);
                      }, 0);
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Todas las acciones</option>
                    <option value="availability_updated">Disponibilidad Mensual Actualizada</option>
                    <option value="template_applied">Plantilla Semanal Aplicada</option>
                    <option value="day_override">Día Específico Modificado</option>
                    <option value="rate_updated">Tarifa de Turno Actualizada</option>
                    <option value="profile_updated">Perfil Profesional Actualizado</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Mes</label>
                  <select
                    value={activityFilter.month}
                    onChange={(e) => {
                      setActivityFilter(prev => ({ ...prev, month: e.target.value }));
                      setActivityPagination(prev => ({ ...prev, page: 1 }));
                      setTimeout(() => {
                        fetchActivityLogs(1);
                      }, 0);
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Todos los meses</option>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                      <option key={month} value={month.toString()}>
                        {new Date(2024, month - 1).toLocaleDateString('es-AR', { month: 'long' })}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Año</label>
                  <select
                    value={activityFilter.year}
                    onChange={(e) => {
                      setActivityFilter(prev => ({ ...prev, year: e.target.value }));
                      setActivityPagination(prev => ({ ...prev, page: 1 }));
                      setTimeout(() => {
                        fetchActivityLogs(1);
                      }, 0);
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Todos los años</option>
                    {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(year => (
                      <option key={year} value={year.toString()}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Activity Logs */}
            <div className="bg-white rounded-lg shadow">
              {activityLogsLoading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="text-gray-500 mt-2">Cargando actividad...</p>
                </div>
              ) : activityLogs.length === 0 ? (
                <div className="p-8 text-center">
                  <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No hay actividad registrada</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Fecha
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Profesional
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Acción
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Detalles
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {activityLogs.map((log) => (
                        <tr key={log.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {new Date(log.createdAt).toLocaleDateString('es-AR', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {log.professional?.name || 'Desconocido'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {log.professional?.email || 'Sin email'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getActionColor(log.action)}`}>
                              {getActionLabel(log.action)}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900 max-w-md">
                              {log.changes_summary}
                            </div>
                            {log.month && log.year && (
                              <div className="text-xs text-gray-500 mt-1">
                                {new Date(log.year, log.month - 1).toLocaleDateString('es-AR', { month: 'long', year: 'numeric' })}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {activityPagination.pages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Mostrando página {activityPagination.page} de {activityPagination.pages} 
                      ({activityPagination.total} registros totales)
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => fetchActivityLogs(activityPagination.page - 1)}
                        disabled={activityPagination.page <= 1}
                        className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Anterior
                      </button>
                      <button
                        onClick={() => fetchActivityLogs(activityPagination.page + 1)}
                        disabled={activityPagination.page >= activityPagination.pages}
                        className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Siguiente
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'reports' && activeSubTab === 'payments' && <AdminRentalPayments />}

        {/* System - Specialties Tab */}
        {activeTab === 'system' && activeSubTab === 'specialties' && (
          <SpecialtiesManager onSpecialtyUpdate={() => {
            // Refresh any related data if needed
            fetchData();
          }} />
        )}

        {/* Appointments - Requests Tab */}
        {activeTab === 'appointments' && activeSubTab === 'requests' && (
          <PreAppointmentsManager onAppointmentUpdate={() => {
            // Refresh any related data if needed
            fetchData();
          }} />
        )}

        {/* Appointments - one professional's agenda: see who took each slot and book a patient into a free one */}
        {activeTab === 'appointments' && activeSubTab === 'agenda' && (
          <div className="space-y-4">
            <label className="flex flex-wrap items-center gap-2 text-sm font-medium text-gray-700">
              Profesional:
              <select className="input h-10 w-auto min-w-[16rem]" value={agendaProfessionalId || String(professionals[0]?.id || '')} onChange={e => setAgendaProfessionalId(e.target.value)}>
                {professionals.map(p => <option key={p.id} value={String(p.id)}>{p.user?.name} · {p.specialty}</option>)}
              </select>
            </label>
            {(agendaProfessionalId || professionals[0]) && (
              <ProfessionalAgenda
                key={agendaProfessionalId || String(professionals[0]?.id)}
                professionalId={agendaProfessionalId || professionals[0]?.id}
                title="Agenda del profesional"
                subtitle="Los horarios de sus consultorios alquilados y quién reservó cada uno. En un horario libre podés agendar un paciente (queda confirmado)."
              />
            )}
          </div>
        )}

        {/* Appointments - Availability Tab */}
        {activeTab === 'appointments' && activeSubTab === 'availability' && <RoomOccupancy />}

      {/* Create Professional Modal */}
      {showCreateProfessional && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Crear Nuevo Profesional</h3>
            <form onSubmit={handleCreateProfessional} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Correo Electrónico</label>
                <input
                  type="email"
                  required
                  value={newProfessional.email}
                  onChange={(e) => setNewProfessional({...newProfessional, email: e.target.value})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Contraseña</label>
                <input
                  type="password"
                  required
                  value={newProfessional.password}
                  onChange={(e) => setNewProfessional({...newProfessional, password: e.target.value})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Nombre</label>
                <input
                  type="text"
                  required
                  value={newProfessional.name}
                  onChange={(e) => setNewProfessional({...newProfessional, name: e.target.value})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Especialidad principal</label>
                <select
                  required
                  value={newProfessional.specialty_id}
                  onChange={(e) => setNewProfessional({...newProfessional, specialty_id: parseInt(e.target.value)})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value={0}>Seleccionar especialidad</option>
                  {specialties.map((specialty) => (
                    <option key={specialty.id} value={specialty.id}>
                      {specialty.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">También atiende (opcional)</label>
                <p className="text-xs text-gray-500">Si hace más de una especialidad, tildalas acá. Los pacientes la van a encontrar en todas.</p>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2">
                  {specialties.filter(sp => sp.id !== newProfessional.specialty_id).map(sp => (
                    <label key={sp.id} className="inline-flex items-center gap-2 text-sm text-gray-700">
                      <input type="checkbox" checked={newProfessional.specialty_ids.includes(sp.id)}
                        onChange={e => setNewProfessional({ ...newProfessional, specialty_ids: e.target.checked ? [...newProfessional.specialty_ids, sp.id] : newProfessional.specialty_ids.filter(x => x !== sp.id) })} />
                      {sp.name}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Teléfono</label>
                <input
                  type="text"
                  value={newProfessional.phone}
                  onChange={(e) => setNewProfessional({...newProfessional, phone: e.target.value})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Biografía</label>
                <textarea
                  value={newProfessional.bio}
                  onChange={(e) => setNewProfessional({...newProfessional, bio: e.target.value})}
                  rows={3}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Orden de Aparición</label>
                <input
                  type="number"
                  min="0"
                  value={newProfessional.sortOrder}
                  onChange={(e) => setNewProfessional({...newProfessional, sortOrder: parseInt(e.target.value) || 0})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
                <p className="text-xs text-gray-500 mt-1">Número menor = aparece primero en la página principal</p>
              </div>
              <div className="flex space-x-3">
                <button
                  type="submit"
                  className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 transition-colors"
                >
                  Crear
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateProfessional(false)}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Professional Confirmation Modal */}
      {showDeleteProfessionalModal && professionalToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div 
            className="bg-white rounded-lg shadow-xl w-full max-w-2xl overflow-hidden"
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            {/* Header */}
            <div className="bg-red-50 border-b border-red-200 px-6 py-4">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <Trash2 className="h-5 w-5 text-red-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-red-900">
                    Confirmar Eliminación de Profesional
                  </h3>
                  <p className="text-sm text-red-700">
                    Esta acción no se puede deshacer
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-6">
              {/* Professional Info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
                    <UserIcon className="h-8 w-8 text-primary-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-xl font-semibold text-gray-900">
                      {professionalToDelete.user?.name || 'Profesional'}
                    </h4>
                    <p className="text-gray-600">{professionalToDelete.specialty}</p>
                    <p className="text-sm text-gray-500">
                      {professionalToDelete.user?.email}
                    </p>
                  </div>
                </div>
              </div>

              {/* Warning Section */}
              <div className="mb-6">
                <div className="flex items-start space-x-3 mb-4">
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center">
                      <span className="text-red-600 text-sm font-bold">⚠️</span>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-red-900 mb-2">
                      ¿Qué se eliminará?
                    </h4>
                    <div className="space-y-2 text-sm text-red-700">
                      <div className="flex items-center space-x-2">
                        <Calendar className="h-4 w-4" />
                        <span>Todas las citas de pacientes programadas</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Clock className="h-4 w-4" />
                        <span>Todos los horarios de disponibilidad configurados</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Building className="h-4 w-4" />
                        <span>Todas las reservas de salas realizadas</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <ClockIcon className="h-4 w-4" />
                        <span>Todos los registros de actividad asociados</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Benefits Section */}
              <div className="mb-6">
                <div className="flex items-start space-x-3 mb-4">
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-green-600 text-sm font-bold">✅</span>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-green-900 mb-2">
                      Beneficios de la eliminación
                    </h4>
                    <div className="space-y-2 text-sm text-green-700">
                      <div className="flex items-center space-x-2">
                        <Calendar className="h-4 w-4" />
                        <span>Las citas se liberarán para que otros profesionales puedan reservar</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Building className="h-4 w-4" />
                        <span>Las salas quedarán disponibles para nuevos horarios</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <ClockIcon className="h-4 w-4" />
                        <span>Se mantendrá un registro de auditoría de la eliminación</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Impact Summary */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <div className="flex items-center space-x-2 mb-2">
                  <span className="text-amber-600 text-sm font-bold">📊</span>
                  <h4 className="text-sm font-medium text-amber-900">
                    Impacto en el Sistema
                  </h4>
                </div>
                <p className="text-sm text-amber-700">
                  Al eliminar a <strong>{professionalToDelete.user?.name}</strong>, se liberarán 
                  todos los horarios y citas asociados, permitiendo que otros profesionales 
                  puedan aprovechar estos espacios y mejorar la utilización de las instalaciones.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 flex flex-col sm:flex-row-reverse sm:space-x-3 sm:space-x-reverse space-y-3 sm:space-y-0">
              <button
                onClick={confirmDeleteProfessional}
                disabled={deletingProfessional}
                className="w-full sm:w-auto inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {deletingProfessional ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Eliminando...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Sí, Eliminar Profesional
                  </>
                )}
              </button>
              <button
                onClick={cancelDeleteProfessional}
                disabled={deletingProfessional}
                className="w-full sm:w-auto inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Cancelar
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Availability Management Modal */}




      {/* Edit Professional Modal */}
      {showEditProfessional && editingProfessional && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Editar Profesional</h3>
            <form onSubmit={handleUpdateProfessional} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Nombre</label>
                <input
                  type="text"
                  required
                  value={editProfessional.name}
                  onChange={(e) => setEditProfessional({...editProfessional, name: e.target.value})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Correo Electrónico</label>
                <input
                  type="email"
                  required
                  value={editProfessional.email}
                  onChange={(e) => setEditProfessional({...editProfessional, email: e.target.value})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Teléfono</label>
                <input
                  type="text"
                  value={editProfessional.phone}
                  onChange={(e) => setEditProfessional({...editProfessional, phone: e.target.value})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Especialidad principal</label>
                <select
                  required
                  value={editProfessional.specialty_id}
                  onChange={(e) => setEditProfessional({...editProfessional, specialty_id: parseInt(e.target.value)})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value={0}>Seleccionar especialidad</option>
                  {specialties.map((specialty) => (
                    <option key={specialty.id} value={specialty.id}>
                      {specialty.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">También atiende (opcional)</label>
                <p className="text-xs text-gray-500">Si hace más de una especialidad, tildalas acá. Los pacientes la van a encontrar en todas.</p>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2">
                  {specialties.filter(sp => sp.id !== editProfessional.specialty_id).map(sp => (
                    <label key={sp.id} className="inline-flex items-center gap-2 text-sm text-gray-700">
                      <input type="checkbox" checked={editProfessional.specialty_ids.includes(sp.id)}
                        onChange={e => setEditProfessional({ ...editProfessional, specialty_ids: e.target.checked ? [...editProfessional.specialty_ids, sp.id] : editProfessional.specialty_ids.filter(x => x !== sp.id) })} />
                      {sp.name}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Biografía</label>
                <textarea
                  value={editProfessional.bio}
                  onChange={(e) => setEditProfessional({...editProfessional, bio: e.target.value})}
                  rows={3}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Orden de Aparición</label>
                <input
                  type="number"
                  min="0"
                  value={editProfessional.sortOrder}
                  onChange={(e) => setEditProfessional({...editProfessional, sortOrder: parseInt(e.target.value) || 0})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
                <p className="text-xs text-gray-500 mt-1">Número menor = aparece primero en la página principal</p>
              </div>
              <div className="flex space-x-3">
                <button
                  type="submit"
                  className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 transition-colors"
                >
                  Actualizar
                </button>
                <button
                  type="button"
                  onClick={() => setShowEditProfessional(false)}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}


      {/* Edit User Modal */}
      {showEditUser && editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Editar Usuario</h3>
              <button
                type="button"
                onClick={() => {
                  setChangingPasswordFor(editingUser);
                  setShowChangePassword(true);
                  setShowEditUser(false);
                }}
                className="inline-flex items-center px-3 py-1 bg-amber-100 text-amber-700 rounded-md text-sm font-medium hover:bg-amber-200 transition-colors"
              >
                <span>Cambiar Contraseña</span>
              </button>
            </div>
            <form onSubmit={handleUpdateUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Nombre</label>
                <input
                  type="text"
                  required
                  value={editUser.name}
                  onChange={(e) => setEditUser({...editUser, name: e.target.value})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Correo Electrónico</label>
                <input
                  type="email"
                  required
                  value={editUser.email}
                  onChange={(e) => setEditUser({...editUser, email: e.target.value})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Teléfono</label>
                <input
                  type="text"
                  value={editUser.phone}
                  onChange={(e) => setEditUser({...editUser, phone: e.target.value})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Rol</label>
                <select
                  value={editUser.role}
                  onChange={(e) => setEditUser({...editUser, role: e.target.value as any})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="user">Paciente</option>
                  <option value="professional">Profesional</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Fecha de Nacimiento</label>
                <ArgentineDatePicker
                  value={editUser.date_of_birth}
                  onChange={(value) => setEditUser({...editUser, date_of_birth: value})}
                  placeholder="dd-MM-YYYY"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Dirección</label>
                <input
                  type="text"
                  value={editUser.address}
                  onChange={(e) => setEditUser({...editUser, address: e.target.value})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              
              {/* Emergency Contact */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Contacto de Emergencia</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Nombre</label>
                    <input
                      type="text"
                      value={editUser.emergency_contact.name}
                      onChange={(e) => setEditUser({
                        ...editUser, 
                        emergency_contact: {...editUser.emergency_contact, name: e.target.value}
                      })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Teléfono</label>
                    <input
                      type="text"
                      value={editUser.emergency_contact.phone}
                      onChange={(e) => setEditUser({
                        ...editUser, 
                        emergency_contact: {...editUser.emergency_contact, phone: e.target.value}
                      })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Relación</label>
                    <input
                      type="text"
                      value={editUser.emergency_contact.relationship}
                      onChange={(e) => setEditUser({
                        ...editUser, 
                        emergency_contact: {...editUser.emergency_contact, relationship: e.target.value}
                      })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex space-x-3">
                <button
                  type="submit"
                  className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 transition-colors"
                >
                  Actualizar
                </button>
                <button
                  type="button"
                  onClick={() => setShowEditUser(false)}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showChangePassword && changingPasswordFor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Cambiar Contraseña - {changingPasswordFor.name}
            </h3>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Nueva Contraseña</label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Mínimo 6 caracteres"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Confirmar Nueva Contraseña</label>
                <input
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Repite la contraseña"
                />
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-amber-800">
                      Esta acción cambiará la contraseña del usuario. El usuario deberá usar la nueva contraseña para iniciar sesión.
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex space-x-3">
                <button
                  type="submit"
                  className="flex-1 bg-amber-600 text-white py-2 px-4 rounded-md hover:bg-amber-700 transition-colors"
                >
                  Cambiar Contraseña
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowChangePassword(false);
                    setChangingPasswordFor(null);
                    setNewPassword('');
                    setConfirmPassword('');
                    // Return to edit user modal if we came from there
                    if (editingUser) {
                      setShowEditUser(true);
                    }
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Service Modal */}
      {showCreateService && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Crear Nuevo Servicio</h3>
            <form onSubmit={handleCreateService} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Nombre del Servicio</label>
                <input
                  type="text"
                  required
                  value={newService.name}
                  onChange={(e) => setNewService({...newService, name: e.target.value})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Descripción</label>
                <textarea
                  value={newService.description}
                  onChange={(e) => setNewService({...newService, description: e.target.value})}
                  rows={3}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Precio</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    required
                    value={newService.price}
                    onChange={(e) => setNewService({...newService, price: parseFloat(e.target.value) || 0})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Moneda</label>
                  <select
                    value={newService.currency}
                    onChange={(e) => setNewService({...newService, currency: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="ARS">ARS</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Duración (minutos)</label>
                <select
                  value={newService.duration_minutes}
                  onChange={(e) => setNewService({...newService, duration_minutes: parseInt(e.target.value)})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value={15}>15 minutos</option>
                  <option value={30}>30 minutos</option>
                  <option value={45}>45 minutos</option>
                  <option value={60}>1 hora</option>
                  <option value={90}>1.5 horas</option>
                  <option value={120}>2 horas</option>
                </select>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="active"
                  checked={newService.active}
                  onChange={(e) => setNewService({...newService, active: e.target.checked})}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="active" className="ml-2 block text-sm text-gray-900">
                  Servicio activo
                </label>
              </div>
              <div className="flex space-x-3">
                <button
                  type="submit"
                  className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 transition-colors"
                >
                  Crear
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateService(false)}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Service Modal */}
      {showEditService && editingService && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Editar Servicio</h3>
            <form onSubmit={handleUpdateService} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Nombre del Servicio</label>
                <input
                  type="text"
                  required
                  value={editService.name}
                  onChange={(e) => setEditService({...editService, name: e.target.value})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Descripción</label>
                <textarea
                  value={editService.description}
                  onChange={(e) => setEditService({...editService, description: e.target.value})}
                  rows={3}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Precio</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    required
                    value={editService.price}
                    onChange={(e) => setEditService({...editService, price: parseFloat(e.target.value) || 0})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Moneda</label>
                  <select
                    value={editService.currency}
                    onChange={(e) => setEditService({...editService, currency: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="ARS">ARS</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Duración (minutos)</label>
                <select
                  value={editService.duration_minutes}
                  onChange={(e) => setEditService({...editService, duration_minutes: parseInt(e.target.value)})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value={15}>15 minutos</option>
                  <option value={30}>30 minutos</option>
                  <option value={45}>45 minutos</option>
                  <option value={60}>1 hora</option>
                  <option value={90}>1.5 horas</option>
                  <option value={120}>2 horas</option>
                </select>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="edit-active"
                  checked={editService.active}
                  onChange={(e) => setEditService({...editService, active: e.target.checked})}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="edit-active" className="ml-2 block text-sm text-gray-900">
                  Servicio activo
                </label>
              </div>
              <div className="flex space-x-3">
                <button
                  type="submit"
                  className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 transition-colors"
                >
                  Actualizar
                </button>
                <button
                  type="button"
                  onClick={() => setShowEditService(false)}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Room Modal */}
      {showCreateRoom && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Crear Nueva Sala</h3>
            <form onSubmit={handleCreateRoom} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Nombre de la Sala</label>
                <input
                  type="text"
                  required
                  value={newRoom.name}
                  onChange={(e) => setNewRoom({...newRoom, name: e.target.value})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Descripción</label>
                <textarea
                  value={newRoom.description}
                  onChange={(e) => setNewRoom({...newRoom, description: e.target.value})}
                  rows={3}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Orden</label>
                <input
                  type="number"
                  min="0"
                  value={newRoom.sort_order}
                  onChange={(e) => setNewRoom({...newRoom, sort_order: parseInt(e.target.value) || 0})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="room-active"
                  checked={newRoom.is_active}
                  onChange={(e) => setNewRoom({...newRoom, is_active: e.target.checked})}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="room-active" className="ml-2 block text-sm text-gray-900">
                  Sala activa
                </label>
              </div>
              <div className="flex space-x-3">
                <button
                  type="submit"
                  className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 transition-colors"
                >
                  Crear
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateRoom(false)}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Room Modal */}
      {showEditRoom && editingRoom && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Editar Sala</h3>
            <form onSubmit={handleUpdateRoom} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Nombre de la Sala</label>
                <input
                  type="text"
                  required
                  value={editRoom.name}
                  onChange={(e) => setEditRoom({...editRoom, name: e.target.value})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Descripción</label>
                <textarea
                  value={editRoom.description}
                  onChange={(e) => setEditRoom({...editRoom, description: e.target.value})}
                  rows={3}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Orden</label>
                <input
                  type="number"
                  min="0"
                  value={editRoom.sort_order}
                  onChange={(e) => setEditRoom({...editRoom, sort_order: parseInt(e.target.value) || 0})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="edit-room-active"
                  checked={editRoom.is_active}
                  onChange={(e) => setEditRoom({...editRoom, is_active: e.target.checked})}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="edit-room-active" className="ml-2 block text-sm text-gray-900">
                  Sala activa
                </label>
              </div>
              <div className="flex space-x-3">
                <button
                  type="submit"
                  className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 transition-colors"
                >
                  Actualizar
                </button>
                <button
                  type="button"
                  onClick={() => setShowEditRoom(false)}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Deduction Rule Modal */}
      {showCreateDeductionRule && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Agregar Regla de Descuento</h3>
            <form onSubmit={handleCreateDeductionRule} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Horas mínimas por día</label>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  required
                  value={newDeductionRule.minHoursPerDay}
                  onChange={(e) => setNewDeductionRule({...newDeductionRule, minHoursPerDay: parseFloat(e.target.value) || 0})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Porcentaje de descuento (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  required
                  value={newDeductionRule.discountPercentage}
                  onChange={(e) => setNewDeductionRule({...newDeductionRule, discountPercentage: parseFloat(e.target.value) || 0})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Descripción</label>
                <textarea
                  required
                  value={newDeductionRule.description}
                  onChange={(e) => setNewDeductionRule({...newDeductionRule, description: e.target.value})}
                  rows={3}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Ej: 10% de descuento para 4+ horas por día"
                />
              </div>
              <div className="flex space-x-3">
                <button
                  type="submit"
                  className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 transition-colors"
                >
                  Crear
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateDeductionRule(false)}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Deduction Rule Modal */}
      {showEditDeductionRule && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Editar Regla de Descuento</h3>
            <form onSubmit={handleUpdateDeductionRule} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Horas mínimas por día</label>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  required
                  value={newDeductionRule.minHoursPerDay}
                  onChange={(e) => setNewDeductionRule({...newDeductionRule, minHoursPerDay: parseFloat(e.target.value) || 0})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Porcentaje de descuento (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  required
                  value={newDeductionRule.discountPercentage}
                  onChange={(e) => setNewDeductionRule({...newDeductionRule, discountPercentage: parseFloat(e.target.value) || 0})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Descripción</label>
                <textarea
                  required
                  value={newDeductionRule.description}
                  onChange={(e) => setNewDeductionRule({...newDeductionRule, description: e.target.value})}
                  rows={3}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Ej: 10% de descuento para 4+ horas por día"
                />
              </div>
              <div className="flex space-x-3">
                <button
                  type="submit"
                  className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 transition-colors"
                >
                  Actualizar
                </button>
                <button
                  type="button"
                  onClick={() => setShowEditDeductionRule(false)}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Monthly Room Availability Modal */}
      {showMonthlyRoomAvailabilityModal && selectedRoomForMonthlyAvailability && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                Gestión de Disponibilidad - {selectedRoomForMonthlyAvailability.name}
              </h3>
              <button
                onClick={() => {
                  setShowMonthlyRoomAvailabilityModal(false);
                  setSelectedRoomForMonthlyAvailability(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            
                         <MonthlyRoomAvailabilityManager 
               roomId={selectedRoomForMonthlyAvailability.id}
               isAdmin={true}
               onRoomAvailabilityChange={(roomId: string, availability: any) => {
                 toast.success(`Disponibilidad actualizada para ${selectedRoomForMonthlyAvailability.name}`);
               }}
             />
          </div>
        </div>
      )}


      </div>
    </motion.div>
  );
};

export default AdminDashboard;